import React, { useState, useEffect } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { clientesAPI, type Cliente, type ClienteMovimiento } from "@/lib/api";
import { getClienteDocumentoActivo } from "@/lib/clienteTipoFactura";
import { formatDateOnlyEsAR, toDateOnlyString, toNum } from "@/lib/utils";
import { useDataRefresh, type RefreshEvent } from "@/lib/dataRefresh";
import { ClienteEstadoCuentaNotas } from "./ClienteEstadoCuentaNotas";
import type { NotasUser } from "./clienteEstadoCuentaNotas.permissions";

const CLIENTE_ESTADO_REFRESH_EVENTS: RefreshEvent[] = ["clientes", "facturas", "ventas"];

function localYmd(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseYmdLocal(ymd: string | null): Date | null {
  if (!ymd) return null;
  const [y, mo, d] = ymd.split("-").map(Number);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d);
}

interface ClienteEstadoCuentaProps {
  cliente: Cliente;
  user?: NotasUser;
}

export function ClienteEstadoCuenta({ cliente, user }: ClienteEstadoCuentaProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("todos");
  const [movimientos, setMovimientos] = useState<ClienteMovimiento[]>([]);
  const [loading, setLoading] = useState(true);
  // Saldo de cabecera = Cliente.saldo (igual que cards); se refresca con el detalle.
  const [saldoActual, setSaldoActual] = useState<number>(Number(cliente.saldo) || 0);

  // Fetch movimientos function
  const fetchMovimientos = React.useCallback(async () => {
    try {
      // Don't set loading on background refreshes if we already have data
      if (movimientos.length === 0) setLoading(true);

      const [estado, clienteFresh] = await Promise.all([
        clientesAPI.getEstadoCuenta(cliente.codigo),
        clientesAPI.get(cliente.codigo).catch(() => null),
      ]);
      setMovimientos(estado.movimientos);
      const fromMov = Number.isFinite(estado.saldoActual) ? estado.saldoActual : null;
      const fromCliente = clienteFresh ? toNum(clienteFresh.saldo) : null;
      if (fromMov != null) setSaldoActual(fromMov);
      else if (fromCliente != null) setSaldoActual(fromCliente);
    } catch (error) {
      console.error("Error loading movimientos:", error);
    } finally {
      setLoading(false);
    }
  }, [cliente.codigo]);

  // Initial fetch
  useEffect(() => {
    setSaldoActual(toNum(cliente.saldo));
    fetchMovimientos();
  }, [fetchMovimientos, cliente.saldo]);

  // Listen for global data refresh events (e.g. new receipt created)
  useDataRefresh(CLIENTE_ESTADO_REFRESH_EVENTS, fetchMovimientos);

  const saldoFinal = saldoActual;

  // Filter logic
  const filteredMovimientos = movimientos.filter(mov => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      mov.referencia.toLowerCase().includes(searchLower) ||
      mov.descripcion.toLowerCase().includes(searchLower) ||
      mov.tipo.toLowerCase().includes(searchLower);

    if (!matchesSearch) return false;

    const movYmd = toDateOnlyString(mov.fecha);
    const movDate = parseYmdLocal(movYmd);
    const today = parseYmdLocal(localYmd())!;

    switch (dateFilter) {
      case "hoy":
        return movYmd === localYmd();
      case "esta-semana": {
        if (!movDate) return false;
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        return movDate >= weekAgo;
      }
      case "este-mes":
        return !!movDate &&
          movDate.getMonth() === today.getMonth() &&
          movDate.getFullYear() === today.getFullYear();
      default:
        return true;
    }
  });

  const formatDate = (dateString: string) => formatDateOnlyEsAR(dateString);

  const getTipoLabel = (tipo: string, confirmationType?: 'cae' | 'fx') => {
    const labels: { [key: string]: string } = {
      'factura_pendiente': 'Factura Pendiente',
      'factura_cae': 'Factura CAE',
      'factura_fx': 'Factura FX',
      'factura_cc': 'Factura CC',
      'pago': 'Pago',
      'cobro_cae': 'Factura CAE', // Display as Invoice Type per user request
      'cobro_fx': 'Factura FX',
      'nota_credito': 'Nota de Crédito',
      'nota_credito_devolucion': 'NC (Devolución)',
      'ajuste': 'Ajuste',
      'rechazo_cheque': 'Cheque rechazado',
    };

    let label = labels[tipo] || tipo;

    // If it's factura_cc and has a confirmation type, append it
    if (tipo === 'factura_cc' && confirmationType) {
      label = `Factura CC / ${confirmationType.toUpperCase()}`;
    }

    return label;
  };

  if (loading) {
    return <div className="p-8 text-center">Cargando movimientos...</div>;
  }

  const doc = getClienteDocumentoActivo(cliente);

  return (
    <div className="space-y-6">
      <div className="border border-input rounded-lg p-6 bg-muted/30">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">Estado de Cuenta</h3>
          <ClienteEstadoCuentaNotas cliente={cliente} user={user} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Cliente</p>
            <p className="font-semibold">{cliente.nombre}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Código</p>
            <p className="font-semibold">{cliente.codigo}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{doc.label}</p>
            <p className="font-semibold">{doc.value}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Saldo Actual</p>
            <p className={`font-bold text-lg ${saldoFinal < -0.001 ? 'text-red-600' : saldoFinal > 0.001 ? 'text-green-600' : 'text-yellow-600'
              }`}>
              ${(Math.abs(saldoFinal) < 0.001 ? 0 : saldoFinal).toLocaleString('es-AR')}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {saldoFinal < -0.001 ? 'Cliente debe' : saldoFinal > 0.001 ? 'Cliente tiene saldo a favor' : 'Saldo en cero'}
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Buscar por referencia, descripción o tipo..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={dateFilter} onValueChange={setDateFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filtrar por fecha" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="hoy">Hoy</SelectItem>
            <SelectItem value="esta-semana">Esta Semana</SelectItem>
            <SelectItem value="este-mes">Este Mes</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredMovimientos.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          {searchQuery || dateFilter !== "todos"
            ? "No se encontraron movimientos con los filtros aplicados"
            : "No hay movimientos registrados para este cliente"}
        </div>
      ) : (
        <div className="border border-input rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium">Fecha</th>
                <th className="text-left py-3 px-4 text-sm font-medium">Tipo</th>
                <th className="text-left py-3 px-4 text-sm font-medium">Referencia</th>
                <th className="text-left py-3 px-4 text-sm font-medium">Descripción</th>
                <th className="text-right py-3 px-4 text-sm font-medium">Debe</th>
                <th className="text-right py-3 px-4 text-sm font-medium">Haber</th>
                <th className="text-right py-3 px-4 text-sm font-medium">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {filteredMovimientos.map((mov) => {
                const displayHaber = mov.haber;

                return (
                  <tr key={mov.id} className="border-t border-input hover:bg-muted/50">
                    <td className="py-3 px-4 text-sm">{formatDate(mov.fecha)}</td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-1 rounded-full text-xs ${mov.tipo === 'factura_pendiente'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : mov.tipo === 'factura_cae'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : mov.tipo === 'factura_fx'
                              ? 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                              : mov.tipo === 'factura_cc'
                                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                                : (mov.tipo === 'pago' || mov.tipo.startsWith('cobro_'))
                                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                  : (mov.tipo === 'nota_credito' || mov.tipo === 'nota_credito_devolucion')
                                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                    : mov.tipo === 'rechazo_cheque'
                                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                                      : 'bg-muted'
                          }`}>
                          {getTipoLabel(mov.tipo, mov.confirmationType)}
                        </span>
                        {((mov.isNC ?? mov.is_nc) || mov.tipo === 'nota_credito' || mov.tipo === 'nota_credito_devolucion') && (mov.tipoComprobante ?? mov.tipo_comprobante) && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-200 text-purple-900 dark:bg-purple-800 dark:text-purple-200">
                            {mov.tipoComprobante ?? mov.tipo_comprobante}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm font-mono">{mov.referencia}</td>
                    <td className="py-3 px-4 text-sm">{mov.descripcion}</td>
                    <td className="py-3 px-4 text-sm text-right">
                      {mov.debe !== 0 && (
                        <span className="text-blue-600">
                          ${Math.abs(mov.debe).toLocaleString('es-AR')}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-right">
                      {displayHaber !== 0 && (
                        <span className="text-red-600">
                          ${Math.abs(displayHaber).toLocaleString('es-AR')}
                        </span>
                      )}
                    </td>
                    <td className={`py-3 px-4 text-sm text-right font-semibold ${mov.saldo < -0.001 ? 'text-red-600' : mov.saldo > 0.001 ? 'text-green-600' : 'text-yellow-600'
                      }`}>
                      ${(Math.abs(mov.saldo) < 0.001 ? 0 : mov.saldo).toLocaleString('es-AR')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-muted border-t-2 border-input">
              <tr>
                <td colSpan={4} className="py-3 px-4 text-sm font-semibold text-right">
                  Totales (Filtrados):
                </td>
                <td className="py-3 px-4 text-sm text-right font-bold">
                  ${filteredMovimientos.reduce((sum, m) => sum + m.debe, 0).toLocaleString('es-AR')}
                </td>
                <td className="py-3 px-4 text-sm text-right font-bold">
                  ${filteredMovimientos.reduce((sum, m) => {
                    // For CC entries, show the pending amount

                    return sum + Math.abs(m.haber);
                  }, 0).toLocaleString('es-AR')}
                </td>
                <td className={`py-3 px-4 text-sm text-right font-bold text-lg ${saldoFinal < -0.001 ? 'text-red-600' : saldoFinal > 0.001 ? 'text-green-600' : 'text-yellow-600'
                  }`}>
                  ${(Math.abs(saldoFinal) < 0.001 ? 0 : saldoFinal).toLocaleString('es-AR')}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="border border-input rounded-lg p-4 bg-muted/20">
        <h4 className="font-semibold mb-2">Información del Saldo</h4>
        <ul className="text-sm space-y-1 text-muted-foreground">
          <li>• <span className="text-red-600 font-semibold">Saldo Negativo</span>: El cliente debe dinero (facturas pendientes de pago)</li>
          <li>• <span className="text-green-600 font-semibold">Saldo Positivo</span>: El cliente tiene saldo a favor (pagos en exceso)</li>
          <li>• <span className="text-yellow-600 font-semibold">Saldo Cero</span>: Cliente al día</li>
          <li className="mt-2 pt-2 border-t">• <span className="font-semibold">Debe</span>: Pagos/Ingresos realizados por el cliente</li>
          <li>• <span className="font-semibold">Haber</span>: Facturas/Egresos que el cliente debe pagar</li>
          <li>• <span className="font-semibold">Saldo</span>: Diferencia acumulada (Debe - Haber)</li>
          <li>• <span className="font-semibold">CC (Cuenta Corriente)</span>: Factura confirmada a cuenta corriente (suma deuda al saldo)</li>
          <li>• <span className="font-semibold">Borrador / Pendiente</span>: No entra al saldo ni al cierre hasta confirmar (el medio de pago se elige al finalizar)</li>
        </ul>
      </div>
    </div>
  );
}